import {
  Dtos_V1_EmployeeDtoFilterInput,
  Dtos_V1_EmployeeDtoSortInput,
  Input_V2_Queries_CrewMembersInput,
  useListAvailableCrewMembersQuery
} from '@/common/infrastructure/gql/generated/schema';

import { format } from 'date-fns';
import { useMemo } from 'react';

interface ListJobCrewMembersFilters {
  skip?: number;
  take?: number;
  where?: Dtos_V1_EmployeeDtoFilterInput;
  order?: Dtos_V1_EmployeeDtoSortInput[];
  customerId?: number;
  jobSiteId?: number;
}

/**
 * Hook to list available crew members for jobs
 * @param filters - Query filters for crew members
 * @returns Crew members data, loading state, error, and refetch function
 */
export const useListCrewMembers = (filters: ListJobCrewMembersFilters = { skip: 0, take: 10 }, startDate?: Date, endDate?: Date) => {
  // API expects Date/LocalDate (yyyy-MM-dd only); datetime strings fail to parse on the server.
  const availableCrewMembersInput: Input_V2_Queries_CrewMembersInput = {
    ...(startDate && { referenceStartDate: format(startDate, 'yyyy-MM-dd') }),
    ...(endDate && { referenceEndDate: format(endDate, 'yyyy-MM-dd') }),
    ...(filters.customerId && { customerId: filters.customerId }),
    ...(filters.jobSiteId && { jobSiteId: filters.jobSiteId })
  };

  const jobCrewMembersFilters = useMemo(
    () => ({
      ...filters,
      availableCrewMembersInput
    }),
    [filters.skip, filters.take, filters.where, filters.order, filters.customerId, filters.jobSiteId, startDate, endDate]
  );

  const { data, loading, error, refetch } = useListAvailableCrewMembersQuery({
    variables: jobCrewMembersFilters,
    fetchPolicy: 'cache-first',
    refetchWritePolicy: 'overwrite'
  });

  const totalCount = data?.availableCrewMembers?.totalCount;
  const crewMembers = data?.availableCrewMembers?.items;

  return { crewMembers, totalCount, loading, error, refetch };
};
